import type { TFunction } from 'i18next';
import DeleteIcon from '../../assets/icons/delete.svg?react';
import EditIcon from '../../assets/icons/edit.svg?react';
import { EditableCell } from './EditableCell';
import { EditingActions } from './EditingActions';
import { PropertiesCell } from './PropertiesCell';
import type { PropertyOption } from './PropertySearch';
import type { TableColumn } from './Table';
import { tertiaryIconButtonStyle } from './buttonStyles';
import { Button } from './base/Button';

// Every room the settings lists show has an id and a name; the name is stored
// on `description`.
interface RoomLike {
  id: string;
  description: string;
}

/**
 * The callbacks the room and intervention-room settings lists both hand their
 * column builders. Each list's own callback type extends this with whatever
 * else its columns need (capacity and priority, for the intervention rooms).
 */
export interface RoomEditingCallbacks<T> {
  editingRoomId: string | null;
  editingName: string;
  openPickerRoomId: string | null;
  onStartEditName: (roomId: string) => void;
  onNameChange: (value: string) => void;
  onSaveName: () => void;
  onCancelEdit: () => void;
  onOpenPicker: (roomId: string) => void;
  onClosePicker: () => void;
  onAddProperty: (roomId: string, propertyId: string) => void;
  onRemoveProperty: (roomId: string, propertyId: string) => void;
  onDelete: (room: T) => void;
  canWrite: boolean;
}

interface RoomNameColumnOptions {
  // Shared with the list's default sort, which has to name the same column.
  key: string;
  header: string;
}

// The name cell is an EditableCell, which carries no order of its own, so the
// column sorts on the underlying description. The name deliberately carries no
// `whiteSpace: nowrap`: a long one wraps inside the column rather than running
// over the cell beside it (REH-355).
export const getRoomNameColumn = <T extends RoomLike>(
  cb: RoomEditingCallbacks<T>,
  { key, header }: RoomNameColumnOptions
): TableColumn<T> => ({
  key,
  header,
  width: 237,
  sortable: true,
  sortValue: (room) => room.description,
  render: (room) => (
    <EditableCell
      value={room.description}
      isEditing={cb.editingRoomId === room.id}
      name={cb.editingName}
      onNameChange={cb.onNameChange}
      onSave={cb.onSaveName}
      onCancel={cb.onCancelEdit}
    />
  )
});

interface RoomNeedsColumnOptions<T> {
  header: string;
  emptyLabel: string;
  // Where the needs hang off this list's room type.
  assignedProperties: (room: T) => PropertyOption[];
  allProperties: PropertyOption[];
}

// Read-only users see the needs a room already has, but nothing that adds one.
export const getRoomNeedsColumn = <T extends RoomLike>(
  cb: RoomEditingCallbacks<T>,
  { header, emptyLabel, assignedProperties, allProperties }: RoomNeedsColumnOptions<T>
): TableColumn<T> => ({
  key: 'needs',
  header,
  cellStyle: { flex: '1 1 0', flexBasis: 0, minWidth: 200 },
  render: (room) => (
    <PropertiesCell
      assignedProperties={assignedProperties(room)}
      allProperties={allProperties}
      emptyLabel={emptyLabel}
      isPickerOpen={cb.openPickerRoomId === room.id}
      isEditing={cb.editingRoomId === room.id}
      onOpenPicker={() => cb.onOpenPicker(room.id)}
      onClosePicker={cb.onClosePicker}
      onAdd={(propertyId) => cb.onAddProperty(room.id, propertyId)}
      onRemove={(propertyId) => cb.onRemoveProperty(room.id, propertyId)}
      canEdit={cb.canWrite}
    />
  )
});

// The edit and delete columns are identical across the settings lists, and are
// built only for users who may write — see the callers' `canWrite` checks.
// Each list styles the icons from its own CSS module.
export const getRowActionColumns = <T extends RoomLike>(
  t: TFunction,
  cb: RoomEditingCallbacks<T>,
  iconClassName: string
): TableColumn<T>[] => [
  {
    key: 'edit',
    header: t('settings-col-edit'),
    width: 100,
    headerAlign: 'center',
    cellStyle: { alignItems: 'center' },
    render: (room) => {
      // The row being renamed shows save and cancel instead of the edit icon.
      if (cb.editingRoomId === room.id) {
        return (
          <EditingActions
            canSave={cb.editingName.trim().length > 0}
            onSave={cb.onSaveName}
            onCancel={cb.onCancelEdit}
          />
        );
      }

      return (
        <Button
          type="tertiary"
          onClick={() => cb.onStartEditName(room.id)}
          styles={{ root: tertiaryIconButtonStyle }}
        >
          <EditIcon className={iconClassName} />
        </Button>
      );
    }
  },
  {
    key: 'delete',
    header: t('settings-col-delete'),
    width: 100,
    headerAlign: 'center',
    cellStyle: { alignItems: 'center' },
    render: (room) => (
      <Button
        type="tertiary"
        onClick={() => cb.onDelete(room)}
        styles={{ root: tertiaryIconButtonStyle }}
      >
        <DeleteIcon className={iconClassName} />
      </Button>
    )
  }
];
